import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { DBPermissionGroupSchema, getPermissionGroupType, PermissionGroupSchema } from './schemas';

type PermissionGroup = z.infer<typeof PermissionGroupSchema>;
type DBPermissionGroup = z.infer<typeof DBPermissionGroupSchema>;

export const PermissionGroupCodecConfig: TransformerConfig<
  typeof PermissionGroupSchema,
  typeof DBPermissionGroupSchema
> = {
  name: 'PermissionGroup',
  clientSchema: PermissionGroupSchema,
  dbSchema: DBPermissionGroupSchema,
  transformers: [
    // id ↔ groupId, and the type its prefix records, which is not stored
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['id', 'type'] as const,
        encode: ({ id }) => (id === undefined ? {} : { groupId: id }),
      },
      decoder: {
        sourceFields: ['groupId'] as const,
        decode: ({ groupId }) => ({ id: groupId, type: getPermissionGroupType(groupId) }),
      },
    }),

    createRenameTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>(
      'label',
      'name'
    ),

    // basePermissionIds: [permissionId] ↔ basePermissions: { permissionId: {} }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['basePermissionIds'] as const,
        encode: ({ basePermissionIds }) => ({
          basePermissions: Object.fromEntries(
            basePermissionIds.map((permission: PermissionGroup['basePermissionIds'][number]) => [
              permission,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['basePermissions'] as const,
        decode: ({ basePermissions = {} }) => ({
          basePermissionIds: Object.keys(basePermissions),
        }),
      },
    }),

    // currPermissionIds: [permissionId] ↔ currPermissions: { permissionId: {} }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['currPermissionIds'] as const,
        encode: ({ currPermissionIds }) => ({
          currPermissions: Object.fromEntries(
            currPermissionIds.map((permission: PermissionGroup['currPermissionIds'][number]) => [
              permission,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['currPermissions'] as const,
        decode: ({ currPermissions = {} }) => ({
          currPermissionIds: Object.keys(currPermissions),
        }),
      },
    }),

    // users: [{ userId, name, type }] ↔ users: { userId: { name, type } }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['users'] as const,
        encode: ({ users }) => ({
          users: Object.fromEntries(
            users.map((user: PermissionGroup['users'][number]) => [
              user.userId,
              { name: user.name, type: user.type },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['users'] as const,
        decode: ({ users = {} }) => ({
          users: Object.entries(users).map(([userId, userData]) => ({
            userId,
            ...(userData as NonNullable<DBPermissionGroup['users']>[string]),
          })),
        }),
      },
    }),
  ],
};

export const encodePermissionGroupToDb = createEncodeToDbFn(PermissionGroupCodecConfig);
export const decodePermissionGroupFromDb = createDecodeFromDbFn(PermissionGroupCodecConfig);
